import React from 'react';

import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import aggregateSpectrograms from '@client/utils/aggregateSpectrograms';
import i18n from '@client/utils/i18n';
import { SpectrogramPoint, SpectrumMixDoc, SpectrumMixLightChannel } from '@ledium/types';
import { ItemList } from '@ledium/ui-common';
import { Alert } from '@mui/material';

type SpectrumMixItem = SpectrumMixDoc & {
    id: string;
    lightChannels: (SpectrumMixLightChannel & { spectrogram: SpectrogramPoint[] })[];
    group?: { name: string };
};

const SpectrumMixerMainMenu = () => {
    const {
        globalState: { selectedGroupId }
    } = useGlobalStore();

    function isLocked(spectrum: SpectrumMixItem) {
        if (spectrum.locked?.reason) {
            if (spectrum.locked.reason === 'RUNNING_PROJECT') {
                return i18n('spectrum_mix_running_project', spectrum.locked.projectName) as string;
            }
        }
        return false as const;
    }

    function isLinked(spectrum: SpectrumMixItem) {
        if (spectrum.linked?.reason === 'LINKED_PROJECT') {
            return i18n('spectrum_mix_linked_project', spectrum.linked.projectName) as string;
        }
        return false as const;
    }

    function chart(item: SpectrumMixItem): SpectrogramPoint[] {
        return aggregateSpectrograms(item.lightChannels);
    }

    function filterItems(item: SpectrumMixItem): boolean {
        if (!selectedGroupId) {
            return true;
        }
        return item.zoneId === selectedGroupId;
    }

    function groupByZone(item: SpectrumMixItem): string {
        return `Zone: ${item.group?.name ?? 'Unknown'}`;
    }

    return (
        <>
            {!selectedGroupId && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {i18n('zone_all_warning')}
                </Alert>
            )}
            <ItemList<SpectrumMixItem>
                chart={chart}
                isLocked={isLocked}
                isLinked={isLinked}
                addNewDisabled={!selectedGroupId}
                filterItems={filterItems}
                groupBy={selectedGroupId ? undefined : groupByZone}
            />
        </>
    );
};

export default SpectrumMixerMainMenu;
